import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

import { safeSessionStorage } from "@/lib/storage";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

let cachedToken: string | null = null;
let pendingToken: Promise<string | null> | null = null;

const FETCH_TIMEOUT_MS = 10_000;

async function fetchCsrfToken(): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const bearer = safeSessionStorage.get("fa-test-token");
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/api/user/csrf-token`,
      {
        credentials: "include",
        cache: "no-store",
        signal: controller.signal,
        headers: {
          Accept: "application/json",
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        },
      },
    );
    if (!res.ok) return null;
    const { token } = (await res.json()) as { token?: unknown };
    return typeof token === "string" && token !== "" ? token : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export function getCsrfToken(): Promise<string | null> {
  if (cachedToken !== null) return Promise.resolve(cachedToken);
  if (pendingToken !== null) return pendingToken;
  const request = fetchCsrfToken().then((token) => {
    // A clearCsrfToken() while in flight means this token may be stale.
    if (pendingToken === request) {
      cachedToken = token;
      pendingToken = null;
    }
    return token;
  });
  pendingToken = request;
  return request;
}

export function clearCsrfToken(): void {
  cachedToken = null;
  pendingToken = null;
}

export function needsCsrfToken(method: string | undefined): boolean {
  return !SAFE_METHODS.has((method ?? "GET").toUpperCase());
}

type RetriableConfig = InternalAxiosRequestConfig & { _csrfRetried?: boolean };

export function attachCsrf(client: AxiosInstance): void {
  client.interceptors.request.use(async (config) => {
    if (needsCsrfToken(config.method)) {
      const token = await getCsrfToken();
      if (token !== null) config.headers.set("X-CSRF-TOKEN", token);
    }
    return config;
  });

  client.interceptors.response.use(undefined, (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (error.response?.status !== 419 || !config || config._csrfRetried) {
      return Promise.reject(error);
    }
    config._csrfRetried = true;
    clearCsrfToken();
    return client.request(config);
  });
}
